/**
 * Online races (ACT HQ t-492, act-babylon DR-0094): this page's side of the race room on
 * the home game server. One quick-join race: the room waits up to 15 s for people, fills
 * the grid with bots and sets one start time. This page drives its own craft as in a solo
 * race and sends it about 20 times a second (and the bots, when it's the one running
 * them); everyone else's arrive here to be drawn. The room's clock is the race's: a few
 * pings work out how far this device's clock is from it.
 */
import { Client, type Room } from "@colyseus/sdk";
import { resolveColyseusEndpoint } from "@act/client-core/network";
import {
  packCraftStates,
  PASS_AWARD_MESSAGE,
  ROOM_NAME_VECTORDRIFT,
  unpackCraftStates,
  VD_ONLINE_MSG,
  VD_ONLINE_PROTOCOL,
  type PassAwardMessage,
  type VdCraftState,
  type VdOnlineLapPayload,
  type VdOnlinePongPayload,
  type VdOnlineRacePayload,
} from "@act/shared";
import { pilot } from "../account/pilot";

/** Pings sent at the start, for the clock. */
const PINGS = 5;
const PING_GAP_MS = 250;

export class OnlineRace {
  /** The room's latest slow state. */
  race: VdOnlineRacePayload | null = null;
  onRace: (race: VdOnlineRacePayload) => void = () => {};
  /** Other crafts, with this device's time they arrived. */
  onStates: (crafts: VdCraftState[], at: number) => void = () => {};
  /** The room closed or the connection dropped for good. */
  onGone: () => void = () => {};
  /** The battle pass's award for this page's race, when it lands (ACT HQ t-1104). */
  onPassAward: (award: PassAwardMessage) => void = () => {};
  /** The room's clock minus this device's (ms), from the ping with the shortest round trip. */
  private offset = 0;
  private bestRtt = Infinity;
  private closed = false;

  private constructor(private readonly room: Room) {
    room.onMessage(VD_ONLINE_MSG.race, (p: VdOnlineRacePayload) => {
      this.race = p;
      // Before the pings answer, take the room's clock from the payload itself.
      if (this.bestRtt === Infinity) this.offset = p.now - Date.now();
      this.onRace(p);
    });
    room.onMessage(VD_ONLINE_MSG.state, (raw: unknown) => {
      const crafts = unpackCraftStates(raw);
      if (crafts) this.onStates(crafts, performance.now());
    });
    room.onMessage(VD_ONLINE_MSG.ping, (p: VdOnlinePongPayload) => {
      const now = Date.now();
      const rtt = now - p.t;
      if (rtt < 0 || rtt >= this.bestRtt) return;
      this.bestRtt = rtt;
      this.offset = p.server - (p.t + rtt / 2);
    });
    room.onMessage(PASS_AWARD_MESSAGE, (award: PassAwardMessage) => this.onPassAward(award));
    room.onMessage("*", () => {});
    room.onLeave(() => {
      this.closed = true;
      this.onGone();
    });
    for (let i = 0; i < PINGS; i++) setTimeout(() => this.ping(), i * PING_GAP_MS);
  }

  /** Join the next race on the home server with this craft. Resolves to the race, or to what went wrong. */
  static async join(ship: string): Promise<OnlineRace | string> {
    const token = await pilot.accessToken();
    if (!token) return "Couldn't sign in to the ACT account.";
    try {
      const client = new Client(resolveColyseusEndpoint());
      const room = await client.joinOrCreate(ROOM_NAME_VECTORDRIFT, {
        authToken: token,
        displayName: pilot.label(),
        protocol: VD_ONLINE_PROTOCOL,
        ship,
      });
      return new OnlineRace(room);
    } catch (e) {
      return e instanceof Error && e.message ? e.message : "Couldn't reach the game server.";
    }
  }

  /** The room's clock now. */
  serverNow(): number {
    return Date.now() + this.offset;
  }

  /** This page's crafts: its own, and the bots when it runs them. */
  sendStates(crafts: readonly VdCraftState[]): void {
    if (!this.closed && crafts.length > 0) this.room.send(VD_ONLINE_MSG.state, packCraftStates(crafts));
  }

  /** A lap crossed by this page's craft or one of its bots. */
  sendLap(slot: number, lap: number): void {
    if (!this.closed) this.room.send(VD_ONLINE_MSG.lap, { slot, lap } satisfies VdOnlineLapPayload);
  }

  leave(): void {
    if (this.closed) return;
    this.closed = true;
    void this.room.leave(true);
  }

  private ping(): void {
    if (!this.closed) this.room.send(VD_ONLINE_MSG.ping, { t: Date.now() });
  }
}
